import axios from 'axios';
import { API_URL } from 'shared/utils/constants';
import {
  IndicatorSectionKey,
  indicatorSectionsInOrder,
  INDICATOR_STAGES_KEY,
  INDICATOR_STAGES,
  IndicatorGroup,
  Indicator,
} from '../ImpactIndicator/utils';
import { capitalizeFirstLetter } from 'shared/utils/stringUtils';
import { getAuthorizationHeader } from 'shared/utils';
import { TrueImpactFile } from 'shared/design-system/components/TrueImpactFileUploadBox';
import {
  RequestState,
  RequestStateSuccess,
} from 'shared/auth/hooks/useRequestState';
import {
  DATES_QUESTION_ID_1,
  DATES_QUESTION_ID_2,
  parseDatesPeriod,
} from '../ReportFinalize/answersUtil';
import { FeedbackQuestionsById } from '../../../NPOFeedback/MakeEdits/api';
import { ReportPeriod } from '../SectionPeriod/utils';
import { ReportStage } from 'donor/portal/pages/FeedbackDashboard/api';
import { createDate } from 'shared/utils/formatUTC';
import { GuidanceTextResponse } from '../utils';
import { isDirectServiceOrEstimateSelectionNotValid } from './IndicatorDetailQuestions/IndicatorFileUpload';
import { getPeriodDuration } from './IndicatorDetailQuestions/IndicatorTimePeriod';

export const orderedIndicatorSectionList = [
  'capacityDevelopment',
  'reach',
  'learn',
  'act',
  'catalyzed',
  'succeed',
] as const;

export const sectionKeyToString = (key: IndicatorSectionKey): string => {
  return key === 'capacityDevelopment'
    ? 'Program Development'
    : capitalizeFirstLetter(key);
};

export type IndicatorDetailsRoutes =
  | 'intro'
  | 'forecast'
  | 'dosage'
  | 'happensAfterProgramYear'
  | 'timePeriod'
  | 'measurement'
  | 'file'
  | 'completed';

export interface IndicatorDetailSectionProps {
  className?: string;
  saving?: boolean;
  indicator: Indicator;
  guidanceText: GuidanceTextResponse | undefined;
  hideBottomButtons?: true;
  requestState: 'init' | 'saving' | 'saved' | 'error';
  reportId: number | string;
  indicators: SectionIndicators;
  isReportPassed: boolean;
  beneficiaryTerm?: string;
  programName?: string;
  intermediaries?: string;
  serviceGoal?: string;
  serviceType?: string;
  allIndicators: ReturnType<typeof mapIndicatorsById>;
  reportPeriod: ReportPeriod;
  saveRequestState: RequestState<unknown>;
  setRoute: React.Dispatch<React.SetStateAction<IndicatorDetailsRoutes>>;
  handleIndicatorSave: (i: IndicatorDetails) => unknown;
  setIndicators: React.Dispatch<React.SetStateAction<SectionIndicators>>;
  selectedSectionKey: IndicatorSectionKey;
  setSelectedSectionKey: React.Dispatch<
    React.SetStateAction<IndicatorSectionKey | undefined>
  >;
  selectedIndex: number;
  setSelectedIndex: React.Dispatch<React.SetStateAction<number>>;
  onNext: (answer?: Partial<IndicatorDetails>) => unknown;
  onPrevious: () => unknown;
}

export interface IndicatorDetails {
  stageId: number;
  indicatorId: number;
  indicatorName: string;
  indicatorDefinition: string;
  forecast?: number | string;
  interventionDosageLevel?: string;
  interventionDosageType?: string;
  interventionDosageNumber?: string;
  finalForecast?: number | string;
  happensAfterProgramYear?: 'Yes' | 'No';
  timePeriod?: 'Immediate' | 'Short term' | 'Medium term' | 'Long term';
  measurement?: string;
  isHuman?: boolean;
  file?: TrueImpactFile | string;
  relevant: boolean;
  level1Forecast?: string;
  level1Guess?: string;
  level1IsHuman?: string;
  level1ForecastResultOne?: string;
}

export const allTimePeriods: NonNullable<IndicatorDetails['timePeriod']>[] = [
  'Immediate',
  'Short term',
  'Medium term',
  'Long term',
];

export const getTimePeriodFromString = (
  timePeriod: string,
): IndicatorDetails['timePeriod'] | undefined => {
  return (
    allTimePeriods.find(tp => tp.toLowerCase() === timePeriod.toLowerCase()) ||
    undefined
  );
};

export type SectionIndicators = {
  [x in IndicatorSectionKey]: IndicatorDetails[];
};

export const getTimePeriodText = (
  timePeriod: IndicatorDetails['timePeriod'],
  startDate?: string | Date,
  endDate?: string | Date,
): string | JSX.Element => {
  const {
    optionZeroYears,
    optionThreeYears,
    optionSeven,
    optionMoreThanSeven,
  } = getPeriodDuration(startDate, endDate);

  switch (timePeriod) {
    case 'Immediate':
      return (
        'Within the reporting period' +
        (optionZeroYears ? ` (${optionZeroYears})` : '')
      );
    case 'Short term':
      return '1-3 years' + (optionThreeYears ? ` (${optionThreeYears})` : '');
    case 'Medium term':
      return '4-7 years' + (optionSeven ? ` (${optionSeven})` : '');
    case 'Long term':
      return (
        '7+ years' + (optionMoreThanSeven ? ` (${optionMoreThanSeven})` : '')
      );
    default:
      return 'Not provided';
  }
};

export const sortIndicatorsIntoSections = (
  indicators: IndicatorDetails[],
): SectionIndicators => {
  const result: SectionIndicators = {
    capacityDevelopment: [],
    act: [],
    learn: [],
    reach: [],
    succeed: [],
    catalyzed: [],
  };

  indicators.forEach(indicator => {
    result[INDICATOR_STAGES_KEY[indicator.stageId]]?.push(indicator);
  });

  return result;
};

const indicatorMissingValue = (
  sectionKey: IndicatorSectionKey,
  indicator: IndicatorDetails,
  isReportPassed: boolean,
): IndicatorDetailsRoutes | undefined => {
  if (
    (isReportPassed && !indicator.finalForecast) ||
    (!isReportPassed && !indicator.forecast)
  ) {
    return 'forecast';
  }
  // if (sectionKey === 'reach' && !indicator.interventionDosageType) {
  //   return 'dosage';
  // }
  if (sectionKey === 'succeed' && !indicator.timePeriod) {
    return 'timePeriod';
  }
  if (!indicator.measurement) {
    return 'measurement';
  }
  if (isDirectServiceOrEstimateSelectionNotValid(sectionKey, indicator)) {
    return 'file';
  }
  return undefined;
};

export const getFirstSectionKey = (
  indicators: SectionIndicators,
  isReportPassed: boolean,
): {
  indicatorIndex: number;
  sectionKey: IndicatorSectionKey;
  questionKey: IndicatorDetailsRoutes;
  firstSectionKey: IndicatorSectionKey;
} => {
  let firstSectionKey: IndicatorSectionKey | undefined = undefined;
  for (let i = 0; i < indicatorSectionsInOrder.length; i++) {
    const sectionKey = indicatorSectionsInOrder[i];
    if (!indicators[sectionKey]?.length) continue;
    if (!firstSectionKey) {
      firstSectionKey = sectionKey;
    }
    for (let j = 0; j < indicators[sectionKey].length; j++) {
      const indicator = indicators[sectionKey][j];
      const questionKey = indicatorMissingValue(
        sectionKey,
        indicator,
        isReportPassed,
      );
      if (questionKey) {
        return {
          firstSectionKey,
          sectionKey,
          indicatorIndex: j,
          questionKey,
        };
      }
    }
  }
  return {
    firstSectionKey: 'succeed',
    sectionKey: 'succeed',
    questionKey: 'completed',
    indicatorIndex: indicators.succeed.length - 1,
  };
};

export const getPreviousSectionKey = (
  indicators: SectionIndicators,
  currentSectionKey: IndicatorSectionKey,
): IndicatorSectionKey | undefined => {
  const currentSectionIndex = orderedIndicatorSectionList.findIndex(
    x => x === currentSectionKey,
  );
  for (let i = currentSectionIndex - 1; i >= 0; i--) {
    const sectionKey = orderedIndicatorSectionList[i];
    if (indicators[sectionKey].length > 0) return sectionKey;
  }
  return undefined;
};

export const getNextSectionKey = (
  indicators: SectionIndicators,
  currentSectionKey: IndicatorSectionKey,
): IndicatorSectionKey | undefined => {
  const currentSectionIndex = orderedIndicatorSectionList.findIndex(
    x => x === currentSectionKey,
  );
  for (
    let i = currentSectionIndex + 1;
    i < orderedIndicatorSectionList.length;
    i++
  ) {
    const sectionKey = orderedIndicatorSectionList[i];
    if (indicators[sectionKey].length > 0) return sectionKey;
  }
  return undefined;
};

export const getPreviousAndNextSectionDetails = (
  indicators: SectionIndicators,
  selectedSectionKey: IndicatorSectionKey,
  selectedIndex: number,
): {
  currentSection: IndicatorDetails[];
  indicatorDetails: IndicatorDetails;
  nextIndicator: IndicatorDetails | undefined;
  previousIndicator: IndicatorDetails | undefined;
  previousIndicatorIndex: number;
  lastIndexOfPreviousSection: number | undefined;
  currentSectionKeyIndex: number;
  nextSectionKey: IndicatorSectionKey | undefined;
  previousSectionKey: IndicatorSectionKey | undefined;
} => {
  const currentSection = indicators[selectedSectionKey];
  const indicatorDetails = currentSection[selectedIndex];
  const nextIndicator = currentSection[selectedIndex + 1];
  const previousIndicatorIndex = selectedIndex - 1;
  const previousIndicator = currentSection[previousIndicatorIndex];
  const currentSectionKeyIndex = orderedIndicatorSectionList.findIndex(
    x => x === selectedSectionKey,
  );
  const nextSectionKey = getNextSectionKey(indicators, selectedSectionKey);
  const previousSectionKey = getPreviousSectionKey(
    indicators,
    selectedSectionKey,
  );
  const lastIndexOfPreviousSection = previousSectionKey
    ? indicators[previousSectionKey].length - 1
    : undefined;
  return {
    currentSection,
    indicatorDetails,
    nextIndicator,
    previousIndicator,
    lastIndexOfPreviousSection:
      lastIndexOfPreviousSection !== undefined &&
      lastIndexOfPreviousSection >= 0
        ? lastIndexOfPreviousSection
        : undefined,
    previousIndicatorIndex,
    currentSectionKeyIndex,
    nextSectionKey,
    previousSectionKey,
  };
};

export const initialEmptyData = {
  capacityDevelopment: [],
  act: [],
  learn: [],
  reach: [],
  succeed: [],
  catalyzed: [],
};

export type FileUploadResponse =
  | {
      success: true;
      id: number;
      url: string;
    }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  | { success: false; id: undefined; url: undefined; error: any };

export const fileUpload = async (
  reportId: number | string,
  file: File,
): Promise<FileUploadResponse> => {
  const formData = new FormData();
  const token = getAuthorizationHeader();
  formData.append('file', file);
  try {
    const response = await axios({
      method: 'post',
      url: `${API_URL}/v4/reports/${reportId}/story_file`,
      data: formData,
      headers: { 'Content-Type': 'multipart/form-data', 'ti-id-token': token },
    });
    const {
      id,
      attributes: { url },
    } = response?.data?.data;
    const picture = { id, url, success: true } as const;
    return picture;
  } catch (error) {
    return { error, success: false, id: undefined, url: undefined };
  }
};

// Uploads a note attachment as a report result document (use_type report_resultdoc).
export const resultFileUpload = async (
  reportId: number | string,
  file: File,
): Promise<FileUploadResponse> => {
  const formData = new FormData();
  const token = getAuthorizationHeader();
  formData.append('file', file);
  try {
    const response = await axios({
      method: 'post',
      url: `${API_URL}/v4/reports/${reportId}/result_files`,
      data: formData,
      headers: { 'Content-Type': 'multipart/form-data', 'ti-id-token': token },
    });
    const {
      id,
      attributes: { url },
    } = response?.data?.data;
    return { id, url, success: true } as const;
  } catch (error) {
    return { error, success: false, id: undefined, url: undefined };
  }
};

export interface ReportAPIResponse {
  dbId: number;
  serviceName: string;
  selectedServiceId: number;
  indicators: {
    stageId: IndicatorDetails['stageId'];
    dbId: IndicatorDetails['indicatorId'];
    name: IndicatorDetails['indicatorName'];
    definition: IndicatorDetails['indicatorDefinition'];
    isHuman: boolean;
  }[];
  npoIndicators: {
    file: string | null;
    forecast: IndicatorDetails['forecast'];
    finalForecast: IndicatorDetails['finalForecast'];
    timePeriod: IndicatorDetails['timePeriod'];
    indicatorId: IndicatorDetails['indicatorId'];
    measurement: IndicatorDetails['measurement'];
    happensAfterProgramYear: IndicatorDetails['happensAfterProgramYear'];
    interventionDosageLevel: IndicatorDetails['interventionDosageLevel'];
    interventionDosageType: IndicatorDetails['interventionDosageType'];
    interventionDosageNumber: IndicatorDetails['interventionDosageNumber'];
    relevant: IndicatorDetails['relevant'];
    level1Forecast: IndicatorDetails['level1Forecast'];
    level1Guess: IndicatorDetails['level1Guess'];
    level1IsHuman: IndicatorDetails['level1IsHuman'];
    level1ForecastResultOne: IndicatorDetails['level1ForecastResultOne'];
  }[];
}

function replaceQuotes(input: string): string {
  let result = '';
  for (let i = 0; i < input.length; i++) {
    // If the current character is a single quote and it's not escaped
    if (input[i] === '\'' && (i === 0 || input[i - 1] !== '\\')) {
      result += '"';
    }
    // If the current character is an escaped single quote
    else if (input[i] === '\'' && i > 0 && input[i - 1] === '\\') {
      if (input[i - 2] === '\\') {
        result = result.slice(0, -2); // Remove the preceding backslash
      } else {
        result = result.slice(0, -1); // Remove the preceding backslash
      }
      result += '\'';
    }
    // If the current character is a double quote
    else if (input[i] === '"') {
      result += '\'';
    }
    // For all other characters
    else {
      result += input[i];
    }
  }
  return result;
}

const parseFileInfo = (
  file: string | undefined | null,
): IndicatorDetails['file'] => {
  if (!file) return undefined;
  try {
    return JSON.parse(file);
  } catch (e) { /* empty */ }
  try {
    return JSON.parse(file.replace(/'/g, '"'));
  } catch (e) { /* empty */ }
  try {
    return JSON.parse(replaceQuotes(file));
  } catch (e) {
    return undefined;
  }
};

export const mapIndicators = (
  reportRequestState: RequestStateSuccess<{ allReports: [ReportAPIResponse] }>,
  capacityDevelopmentEnabled: boolean,
  learnEnabled: boolean,
  actEnabled: boolean,
  isReportPassed: boolean,
): IndicatorDetails[] => {
  const response = reportRequestState.data.allReports[0];
  const result: IndicatorDetails[] = [];
  for (const {
    dbId,
    name,
    stageId,
    definition,
    isHuman,
  } of response.indicators) {
    if (
      (!capacityDevelopmentEnabled &&
        stageId === INDICATOR_STAGES.capacityDevelopment) ||
      (!learnEnabled && stageId === INDICATOR_STAGES.learn) ||
      (!actEnabled && stageId === INDICATOR_STAGES.act)
    ) {
      continue;
    }
    const indicatorDetails = response.npoIndicators.find(
      x => x.indicatorId === dbId,
    );
    result.push({
      indicatorId: dbId,
      indicatorName: name,
      stageId,
      isHuman,
      indicatorDefinition: definition,
      file: parseFileInfo(indicatorDetails?.file),
      interventionDosageLevel: indicatorDetails?.interventionDosageLevel,
      interventionDosageType: indicatorDetails?.interventionDosageType,
      interventionDosageNumber: indicatorDetails?.interventionDosageNumber,
      forecast: indicatorDetails?.forecast,
      finalForecast: indicatorDetails?.finalForecast,
      timePeriod: indicatorDetails?.timePeriod,
      measurement: indicatorDetails?.measurement,
      happensAfterProgramYear: indicatorDetails?.happensAfterProgramYear,
      level1Forecast: indicatorDetails?.level1Forecast,
      level1Guess: indicatorDetails?.level1Guess,
      level1IsHuman: indicatorDetails?.level1IsHuman,
      level1ForecastResultOne: indicatorDetails?.level1ForecastResultOne,
      relevant: indicatorDetails?.relevant || true,
    });
  }
  return result;
};

type ReportBuilderAnswers = {
  [id: number]: string;
} & {
  50004: { text: number; answerId: number; type: 'number' };
  50005: { text: number; answerId: number; type: 'number' };
  50006: { text: number; answerId: number; type: 'number' };
};

const currentYear = new Date().getFullYear();
export const isReportPassed = (
  endDate: string | Date | undefined,
  reportYear: number | undefined,
  reportStage: ReportStage | undefined,
) => {
  if (!endDate) {
    if (!reportYear) return false;
    return currentYear > reportYear;
  }
  const end = typeof endDate === 'string' ? createDate(endDate) : endDate;
  if (!end) return false;
  return new Date() > end;
};
export const isReportDatesPassed = (
  answers: ReportBuilderAnswers,
  reportYear: number | undefined,
  reportStage: ReportStage | undefined,
  reportEndDate: string | Date | undefined,
): boolean => {
  const dates = parseDatesPeriod(
    answers[DATES_QUESTION_ID_1] || answers[DATES_QUESTION_ID_2],
  );
  return isReportPassed(
    dates.endDate === 'Unknown'
      ? reportEndDate
      : dates.endDate || reportEndDate,
    reportYear,
    reportStage,
  );
};

export const isReportDatesPassedWithQuestions = (
  answers: FeedbackQuestionsById,
): boolean => {
  const dates = parseDatesPeriod(
    answers[DATES_QUESTION_ID_1]?.text || answers[DATES_QUESTION_ID_2]?.text,
  );
  if (!dates.endDate || typeof dates.endDate === 'string') return false;

  return new Date() > dates.endDate;
};

export const mapIndicatorsById = (groups: IndicatorGroup[]) => {
  const result: { [id: number]: Indicator } = {};
  if (!groups) return result;
  for (let i = 0; i < groups.length; i++) {
    const group = groups[i];
    for (let j = 0; j < group.indicators.length; j++) {
      const indicator = group.indicators[j];
      result[indicator.id] = indicator;
    }
  }
  return result;
};
